import React, { FC, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { IEnrichedProduct, ISimpleProduct } from '$models';
import { Flex, Grid } from '$components/layouts';
import { VariantSelectorMobile } from './variant-selector';
import { SidePanel } from '$components/elements/side-panel';
import { ModalTrackingTypes } from '$constants';
import { useTranslation } from '$hooks';
import { RichText } from '$components/elements/rich-text';
import { SvgIcon } from '$components/elements/svg-icon';
import { Text } from '$components/elements/text';

type Props = {
    selectedVariant: IEnrichedProduct;
    variants: ISimpleProduct[];
    formatInformation?: string;
};

export const BookVariant: FC<Props> = ({ selectedVariant, variants, formatInformation }: Props): JSX.Element => {
    const { product } = selectedVariant;
    const [showModal, setShowModal] = useState(false);
    const [showAllVariants, setShowAllVariants] = useState(false);
    const { translate } = useTranslation();
    const allVariants = useMemo(() => {
        if (showAllVariants || variants.length <= 2) {
            return variants;
        }
        return variants.slice(0, 2);
    }, [variants, showAllVariants]);

    return (
        <>
            <ProductDetailsMargin large={variants.length > 1}>
                <Grid flow="row" columns="1fr" rowGap="extrasmall" columnGap="extrasmall">
                    {allVariants?.map((variant) => (
                        <VariantSelectorMobile
                            key={variant?.itemNumber}
                            variant={variant}
                            selectedVariantNumber={product?.itemNumber}
                        />
                    ))}
                    {variants.length > 2 && !showAllVariants && (
                        <ShowAllSelector onClick={() => setShowAllVariants(true)}>
                            <Flex gap="small" alignItems="center">
                                <SvgIcon size={24} svg="dots" />

                                <Text variant="bodyLarge">
                                    {translate('productPage.productDetails.showMoreVariantsLabel')}
                                </Text>
                            </Flex>
                        </ShowAllSelector>
                    )}
                </Grid>
                {variants.length > 1 && formatInformation && (
                    <ModalOpenButton onClick={() => setShowModal(true)}>
                        {translate('productPage.productDetails.chooseBetweenFormats')}
                    </ModalOpenButton>
                )}
            </ProductDetailsMargin>
            <SidePanel
                show={showModal}
                variant="richtext"
                onChange={(change) => setShowModal(change)}
                title={translate('productPage.productDetails.modalFormatInformationTitle')}
                modalTrackType={ModalTrackingTypes.BookFormats}
            >
                <RichText noPadding text={formatInformation} />
            </SidePanel>
        </>
    );
};

type ProductDetailsMarginProps = {
    large?: boolean;
};

const ProductDetailsMargin = styled.div<ProductDetailsMarginProps>(({ theme, large }) => ({
    marginBottom: large ? theme.space[9] : theme.space[6],
}));

const ModalOpenButton = styled.button(({ theme }) => ({
    backgroundColor: theme.colors.white,
    border: 'none',
    color: theme.colors.red,
    cursor: 'pointer',
    font: theme.fontFamilies.regular,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.regular,
    outline: 'none',
    textDecoration: 'underline',
    padding: 0,
    margin: `${theme.space[3]} ${theme.space[1]} ${theme.space[1]}`,
}));

const ShowAllSelector = styled.div(({ theme }) => ({
    position: 'relative',
    border: `1px solid ${theme.colors.grey5}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: theme.space[1],
    color: theme.colors.black,
    height: '100%',
    cursor: 'pointer',
    borderRadius: theme.general.borderRadius,
    padding: theme.space[4],
    opacity: 1,
    transition: 'height 200ms ease-in-out, opacity 150ms ease-in-out 50ms',
    width: '100%',

    '&:hover': {
        backgroundColor: theme.colors.grey5,
    },
}));
